import React, { useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import { Draggable } from 'react-beautiful-dnd'
import Markdown from 'markdown-to-jsx'
import { Button } from '@/components/ui/button'
import { GripVertical, Pencil, Trash2, Settings, X, Check, Loader2 } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import {
  EditableDescription,
  EditableDescriptionProvider,
  useEditableDescription,
} from './EditableDescription'
import {
  EditableTitle,
  EditableTitleProvider,
} from './EditableTitle'
import {
  sectionCountForStyle,
  splitSections,
  joinSections,
  setSection,
} from '../utils/sectionMarker'

const GET_PAGE_GRID_THUMBNAILS = gql`
  query GET_PAGE_GRID_THUMBNAILS {
    findManyPage(take: 200) {
      id
      hero_image {
        src
      }
    }
  }
`

const GET_PRODUCT_GRID_THUMBNAILS = gql`
  query GET_PRODUCT_GRID_THUMBNAILS {
    findManyProductContainer(take: 100) {
      id
      single_product {
        images {
          src_thumbnail
        }
      }
      advanced_product {
        images {
          src_thumbnail
        }
      }
    }
  }
`

export interface ContentBlockItem {
  id: number
  type: string
  style: string | null
  arrangement: number | null
  video_type?: string | null
  video_link?: string | null
  pageId?: number | null
  page?: {
    id: string
    code: string
    page_profiles: { title: string; language: string }[]
  } | null
  content_block_profiles: {
    id: number
    name: string
    description?: string | null
    description_format?: string | null
    language: string
  }[]
  main_image?: { id: number; src: string } | null
  gallery_images?: { id: number; src: string }[]
  text_color?: string | null
  background_color?: string | null
  config?: Record<string, any> | null
  // PDP attachment ("shown on these products' pages"). NOT the manual
  // product_grid picks — see `grid_product_containers` below.
  product_containers?: { id: number }[] | null
  // Manual `product_grid` picks, ordered. Used by the grid preview below.
  grid_product_containers?: { id: number }[] | null
  categories?: {
    id: number
    image?: { id: number; src: string } | null
    banners?: { id: number; src: string }[]
    category_profiles: { id: number; name: string; language: string }[]
  }[]
}

interface Props {
  block: ContentBlockItem
  index: number
  onRemove: (blockId: number) => void
  onEdit?: (blockId: number) => void
  /**
   * Full-size (page editor) only: deselect the block and return to page
   * settings. Used by the in-overlay Close (✕) button that replaces the gear
   * while the block is selected. Optional — thumbnail consumers don't pass it.
   */
  onClose?: () => void
  /** Render the preview at realistic full size (page editor) instead of the compact thumbnail. */
  fullSize?: boolean
  /** When true, this block is the one currently open in the editor — highlights the card. */
  selected?: boolean
  /**
   * Called after an inline description/title edit (full-size only) with the
   * merged block so the owner can update its block state for an immediate
   * (optimistic) preview. Optional — the thumbnail consumers (ContentBlocksManager,
   * CategoryForm) don't pass it and inline editing stays disabled there.
   */
  onDescriptionSaved?: (block: ContentBlockItem) => void
  /**
   * STAGE an inline description edit into the page's per-block draft (full-size
   * page editor only) instead of writing it to the server immediately. The
   * page-level Save later flushes it via `UPDATE_CONTENT_BLOCK_PROFILE`.
   * Optional/unreachable on the thumbnail path (inline editing is disabled
   * there — see `onDescriptionSaved`).
   */
  onStageDescription?: (blockId: number, language: string, value: string) => void
  /** Same as `onStageDescription`, for an inline title edit. */
  onStageName?: (blockId: number, language: string, value: string) => void
}

function getBlockName(block: ContentBlockItem): string {
  const profile = block.content_block_profiles.find(p => p.language === 'en')
  return (
    profile?.name ||
    block.content_block_profiles[0]?.name ||
    `Block #${block.id}`
  )
}

function getBlockDescription(block: ContentBlockItem): string | null {
  const profile = block.content_block_profiles.find(p => p.language === 'en')
  return (
    profile?.description || block.content_block_profiles[0]?.description || null
  )
}

type TitleAlign = 'left' | 'center' | 'right'

// Read the block-level `config` object defensively (it may arrive as a parsed
// object or a JSON string at runtime). Returns `{}` when absent/invalid. Used to
// reflect the block-level title settings (`titleAlignment` / `hideTitle`).
function getBlockConfig(block: ContentBlockItem): Record<string, unknown> {
  const raw = (block as { config?: unknown }).config
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw)
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, unknown>)
        : {}
    } catch {
      return {}
    }
  }
  return {}
}

const mockupClass =
  'w-full h-28 border border-gray-200 rounded bg-gray-50 p-2 overflow-hidden'

// Full-size container: realistic width, no fixed thumbnail height, generous
// padding mirroring the live portal's section spacing.
const fullSizeClass =
  'w-full border border-gray-200 rounded bg-white p-6 overflow-hidden'

/**
 * The ONE markdown-to-jsx rendering pipeline shared by every text-preview
 * style (`TextContent`'s multi-column callers included). Extracted out of
 * `MarkdownPreviewText` so `TextContent` renders sections through the exact
 * same overrides/options instead of showing raw markdown source — do not add
 * a second, divergent rendering path here.
 */
function MarkdownBody({
  description,
  fullSize,
  inverse,
  textColor,
  className,
}: {
  description: string
  fullSize?: boolean
  inverse?: boolean
  textColor?: string
  className?: string
}) {
  const baseTextClass = inverse ? 'text-white' : fullSize ? 'text-gray-600' : 'text-gray-500'
  const sizeClass = fullSize ? 'text-base leading-6' : 'text-[9px] leading-tight'
  const headingSizeClass = fullSize ? 'text-xl leading-7' : 'text-[9px] leading-tight'
  const headingClass = `block ${headingSizeClass} font-semibold ${fullSize ? 'mb-2' : 'm-0'} ${
    inverse ? 'text-white' : 'text-gray-700'
  }`
  const inlineBlockClass = `block ${sizeClass} ${fullSize ? 'mb-2' : 'm-0'} ${baseTextClass}`
  const listClass = `block ${sizeClass} ${fullSize ? 'mb-2' : 'm-0'} pl-${fullSize ? '5' : '2'} ${baseTextClass}`
  const itemClass = `block ${sizeClass} ${fullSize ? 'mb-1' : 'm-0'} ${baseTextClass}`

  const textColorStyle = textColor ? { style: { color: textColor } } : {}
  const headingProps = { className: headingClass, ...textColorStyle }
  const paragraphProps = { className: inlineBlockClass, ...textColorStyle }
  const listProps = { className: listClass, ...textColorStyle }
  const itemProps = { className: itemClass, ...textColorStyle }

  return (
    <div
      className={`overflow-hidden ${sizeClass} ${baseTextClass} ${
        className ?? ''
      }`}
    >
      <Markdown
        options={{
          disableParsingRawHTML: true,
          forceBlock: true,
          overrides: {
            h1: { props: headingProps },
            h2: { props: headingProps },
            h3: { props: headingProps },
            h4: { props: headingProps },
            h5: { props: headingProps },
            h6: { props: headingProps },
            p: { props: paragraphProps },
            ul: { props: listProps },
            ol: { props: listProps },
            li: { props: itemProps },
            strong: {
              props: { className: 'font-semibold' },
            },
            em: { props: { className: 'italic' } },
            a: {
              props: {
                className: 'underline',
                onClick: (e: React.MouseEvent) => e.preventDefault(),
              },
            },
            img: {
              props: { className: 'hidden' },
            },
            blockquote: { props: paragraphProps },
            code: {
              props: {
                className: `${sizeClass} ${
                  inverse ? 'bg-white/20' : 'bg-gray-100'
                } rounded px-0.5`,
              },
            },
            pre: { props: paragraphProps },
            hr: { props: { className: 'hidden' } },
          },
        }}
      >
        {description}
      </Markdown>
    </div>
  )
}

function TextContent({
  block,
  fullSize,
  sectionIndex = 0,
}: {
  block: ContentBlockItem
  fullSize?: boolean
  /**
   * Which text section this box owns for multi-section styles. 0-based, and
   * always < `sectionCountForStyle(style)` (2 or 3 today).
   */
  sectionIndex?: number
}) {
  // Inline-edit context (present only in the full-size page editor). When set,
  // display + edit the CURRENT language's description; otherwise fall back to
  // the original English-only rendering (thumbnail path) — unchanged.
  const editCtx = useEditableDescription()
  // Multi-section styles pack N columns into one description string; slice out
  // this box's own (delimiter-free) section. Single-section styles use the whole
  // value, so behaviour is byte-for-byte unchanged for every other style.
  const sectionCount = editCtx
    ? editCtx.sectionCount
    : sectionCountForStyle(getPreviewKey(block))
  const rawSource = editCtx
    ? editCtx.rawValue
    : getBlockDescription(block) ?? ''
  const sliceRaw =
    sectionCount <= 1 ? rawSource : splitSections(rawSource)[sectionIndex] ?? ''
  const description = sliceRaw || null

  const placeholder = (
    <div className={fullSize ? 'flex flex-col gap-2' : 'flex flex-col gap-0.5'}>
      <div
        className={
          fullSize
            ? 'h-4 w-3/4 rounded bg-gray-300'
            : 'h-1.5 w-3/4 rounded-sm bg-gray-400'
        }
      />
      <div
        className={
          fullSize
            ? 'h-3 w-full rounded bg-gray-200'
            : 'h-1 w-full rounded-sm bg-gray-300'
        }
      />
      <div
        className={
          fullSize
            ? 'h-3 w-2/3 rounded bg-gray-200'
            : 'h-1 w-2/3 rounded-sm bg-gray-300'
        }
      />
    </div>
  )

  // Render through the SAME markdown-to-jsx pipeline as every other (working)
  // style's preview (`MarkdownPreviewText`) — previously this rendered
  // `stripHtml(description)` as literal text, so a `####` heading (or any
  // other markdown syntax) showed up as raw source instead of being parsed.
  const body = description ? (
    <MarkdownBody description={description} fullSize={fullSize} />
  ) : (
    placeholder
  )

  if (editCtx) {
    // Multi-section styles (2-/3-column text): all section editors open, edit,
    // and close as ONE unit via the block-level `multi` controller and a single
    // shared Save/Cancel row (rendered by SharedSectionActions). Single-section
    // styles keep their own self-contained inline editor — unchanged.
    if (editCtx.sectionCount > 1) {
      return (
        <EditableDescription
          rawValue={sliceRaw}
          editable={editCtx.editable}
          controlled={{
            editing: editCtx.multi.editing,
            value: editCtx.multi.drafts[sectionIndex] ?? '',
            saving: editCtx.multi.saving,
            onChange: next => editCtx.multi.setDraft(sectionIndex, next),
            onStartEditing: editCtx.multi.start,
            onCancel: editCtx.multi.cancel,
            autoFocus: sectionIndex === 0,
          }}
        >
          {body}
        </EditableDescription>
      )
    }
    return (
      <EditableDescription
        rawValue={sliceRaw}
        editable={editCtx.editable}
        onSave={next => editCtx.onSaveSection(sectionIndex, next)}
      >
        {body}
      </EditableDescription>
    )
  }

  return body
}

/**
 * The single shared confirm / discard row for the multi-column text styles.
 * Rendered once, below ALL of the style's section editors. No-op (returns null) unless
 * we're inside a full-size preview whose block is a multi-section style AND
 * the group is open — so the thumbnail path and single-section styles never
 * see it. Icon-only (X discard / ✓ confirm into the page's draft — no server
 * write happens here, see `DraggableContentBlockItem.saveMultiEdit`).
 */
function SharedSectionActions() {
  const editCtx = useEditableDescription()
  if (
    !editCtx ||
    !editCtx.editable ||
    editCtx.sectionCount <= 1 ||
    !editCtx.multi.editing
  ) {
    return null
  }
  const { saving, cancel, save } = editCtx.multi
  return (
    <div
      className="mt-2 flex items-center justify-end gap-2"
      // Clicks on the row must not bubble to a parent click-to-edit region.
      onClick={e => e.stopPropagation()}
    >
      {saving && (
        <Loader2 className="mr-auto h-4 w-4 animate-spin text-gray-400" />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        title="Discard changes"
        aria-label="Discard changes"
        disabled={saving}
        onClick={cancel}
        className="h-7 w-7"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        size="icon"
        title="Confirm changes"
        aria-label="Confirm changes"
        disabled={saving}
        onClick={save}
        className="h-7 w-7"
      >
        <Check className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

/**
 * The multi-column text styles (`2_columns_title_top_center`,
 * `2_columns_1_3`, `3_columns_title_left`) render N independent text columns
 * and NO title — every column behaves identically, so there is nothing
 * style-specific left but the column count (and, for `2_columns_1_3` only,
 * unequal thumbnail widths). All three styles share this one component so the
 * editor can't drift between them.
 *
 * `columns` MUST equal `sectionCountForStyle(style)` AND the column count the
 * portal renderer maps across (`MultiColumnText` in
 * `vilmers-b2b-portal/src/modules/home/components/content-block/index.tsx`).
 * If they disagree, the editor shows a different layout than the live site —
 * which is exactly the bug this replaced.
 */
function MultiColumnTextPreview({
  block,
  fullSize,
  columns,
  thumbWidths,
}: {
  block: ContentBlockItem
  fullSize?: boolean
  columns: 2 | 3
  /**
   * Optional per-column thumbnail width override for unequal-width variants
   * (e.g. `2_columns_1_3`'s 1/3 + 2/3 split). Length must equal `columns`.
   * Only affects the compact (non-`fullSize`) thumbnail — the full-size
   * editor still uses an equal grid, since the unequal split is a
   * portal-rendering concern only (admin editing behaves like any other
   * 2-section text style). Defaults to N equal columns.
   */
  thumbWidths?: string[]
}) {
  const gridClass = columns === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'
  const thumbWidthClass = columns === 3 ? 'w-1/3' : 'w-1/2'

  return (
    <PreviewContainer fullSize={fullSize}>
      <div
        className={
          fullSize ? 'flex flex-col gap-6' : 'flex h-full flex-col gap-0.5'
        }
      >
        <div
          className={
            fullSize
              ? `grid grid-cols-1 gap-8 ${gridClass}`
              : 'flex flex-1 gap-1'
          }
        >
          {Array.from({ length: columns }, (_, i) => (
            <div
              key={i}
              className={
                fullSize
                  ? 'flex flex-col'
                  : `flex ${thumbWidths?.[i] ?? thumbWidthClass} flex-col justify-start px-0.5`
              }
            >
              <TextContent block={block} fullSize={fullSize} sectionIndex={i} />
            </div>
          ))}
        </div>
        <SharedSectionActions />
      </div>
    </PreviewContainer>
  )
}

/**
 * Compact markdown renderer for the Text & Image style previews.
 * Flattens all block-level markdown (headings, lists, paragraphs) to a
 * single tiny text style with no margins so the thumbnail row height
 * stays bounded regardless of input markup.
 */
function MarkdownPreviewText({
  description,
  className,
  inverse,
  fullSize,
  textColor,
}: {
  description: string | null
  className?: string
  inverse?: boolean
  fullSize?: boolean
  textColor?: string
}) {
  // Inline-edit context (full-size page editor only). When present, display +
  // edit the CURRENT language's raw markdown; otherwise render the `description`
  // prop as before (thumbnail path) — unchanged.
  //
  // MarkdownPreviewText only serves single-section styles, so it always owns
  // section 0; the slice guards keep the delimiter out if a multi-section style
  // is ever routed here.
  const editCtx = useEditableDescription()
  const sliceRaw = editCtx
    ? editCtx.sectionCount <= 1
      ? editCtx.rawValue
      : splitSections(editCtx.rawValue)[0] ?? ''
    : null
  const effectiveDescription = editCtx ? sliceRaw || null : description

  const placeholder = (
    <div className={fullSize ? 'flex flex-col gap-2' : 'flex flex-col gap-0.5'}>
      <div
        className={
          fullSize
            ? `h-4 w-3/4 rounded ${inverse ? 'bg-white/70' : 'bg-gray-300'}`
            : `h-1.5 w-3/4 rounded-sm ${inverse ? 'bg-white/70' : 'bg-gray-400'}`
        }
      />
      <div
        className={
          fullSize
            ? `h-3 w-full rounded ${inverse ? 'bg-white/50' : 'bg-gray-200'}`
            : `h-1 w-full rounded-sm ${inverse ? 'bg-white/50' : 'bg-gray-300'}`
        }
      />
      <div
        className={
          fullSize
            ? `h-3 w-2/3 rounded ${inverse ? 'bg-white/50' : 'bg-gray-200'}`
            : `h-1 w-2/3 rounded-sm ${inverse ? 'bg-white/50' : 'bg-gray-300'}`
        }
      />
    </div>
  )

  if (!effectiveDescription) {
    // Editable, current-language has no description yet → click-to-add.
    if (editCtx) {
      return (
        <EditableDescription
          rawValue=""
          editable={editCtx.editable}
          onSave={next => editCtx.onSaveSection(0, next)}
          inverse={inverse}
        >
          {placeholder}
        </EditableDescription>
      )
    }
    return placeholder
  }

  // Shared with `TextContent`'s multi-column sections — see `MarkdownBody`.
  const body = (
    <MarkdownBody
      description={effectiveDescription}
      fullSize={fullSize}
      inverse={inverse}
      textColor={textColor}
      className={className}
    />
  )

  if (editCtx) {
    return (
      <EditableDescription
        rawValue={sliceRaw ?? ''}
        editable={editCtx.editable}
        onSave={next => editCtx.onSaveSection(0, next)}
        inverse={inverse}
      >
        {body}
      </EditableDescription>
    )
  }

  return body
}

function ImageContent({
  src,
  className,
  fullSize,
}: {
  src?: string | null
  className?: string
  fullSize?: boolean
}) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={
          className ||
          (fullSize
            ? 'h-full w-full rounded object-cover'
            : 'h-full w-full rounded-sm object-cover')
        }
      />
    )
  }
  return (
    <div
      className={
        fullSize ? 'h-full w-full rounded bg-gray-200' : 'h-full w-full rounded-sm bg-gray-300'
      }
    />
  )
}

function PlayButton({ fullSize }: { fullSize?: boolean }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div
        className={
          fullSize
            ? 'h-16 w-16 rounded-full border-2 border-gray-500 flex items-center justify-center'
            : 'h-4 w-4 rounded-full border-2 border-gray-500 flex items-center justify-center'
        }
      >
        <div
          className={
            fullSize
              ? 'ml-1 h-0 w-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-gray-500'
              : 'ml-0.5 h-0 w-0 border-y-[3px] border-l-[5px] border-y-transparent border-l-gray-500'
          }
        />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Reusable layout components (private to this file)                  */
/* ------------------------------------------------------------------ */

function PreviewContainer({
  children,
  fullSize,
}: {
  children: React.ReactNode
  fullSize?: boolean
}) {
  return <div className={fullSize ? fullSizeClass : mockupClass}>{children}</div>
}

// Image/text width ratio for `side_by_side` & `image_left` previews. Semantics
// are image:text — `60_40` = image 60% / text 40%. Classes are FULL LITERALS
// (no interpolation) so Tailwind's JIT source scan keeps them. `_FULL` variants
// carry the `md:` prefix used by the full-size preview; `_THUMB` variants are
// unprefixed for the small mockup thumbnail.
type MediaRatio = '50_50' | '60_40' | '40_60'

const PREVIEW_IMG_W_FULL: Record<MediaRatio, string> = {
  '50_50': 'md:w-1/2',
  '60_40': 'md:w-3/5',
  '40_60': 'md:w-2/5',
}
const PREVIEW_TEXT_W_FULL: Record<MediaRatio, string> = {
  '50_50': 'md:w-1/2',
  '60_40': 'md:w-2/5',
  '40_60': 'md:w-3/5',
}
const PREVIEW_IMG_W_THUMB: Record<MediaRatio, string> = {
  '50_50': 'w-1/2',
  '60_40': 'w-3/5',
  '40_60': 'w-2/5',
}
const PREVIEW_TEXT_W_THUMB: Record<MediaRatio, string> = {
  '50_50': 'w-1/2',
  '60_40': 'w-2/5',
  '40_60': 'w-3/5',
}

// Read `mediaRatio` off the block's `config`, which may be a JS object or a
// JSON string at runtime (mirrors the defensive parse used by the grid
// previews). Falls back to '50_50' for missing/invalid values.
function getMediaRatio(block: ContentBlockItem): MediaRatio {
  const rawConfig = (block as { config?: unknown }).config
  let config: { mediaRatio?: unknown } | null = null
  if (rawConfig && typeof rawConfig === 'object') {
    config = rawConfig as { mediaRatio?: unknown }
  } else if (typeof rawConfig === 'string') {
    try {
      config = JSON.parse(rawConfig)
    } catch {
      config = null
    }
  }
  const ratio = config?.mediaRatio
  return ratio === '60_40' || ratio === '40_60' ? ratio : '50_50'
}

function SideBySidePreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  // Text & Image, side-by-side: text left, image right.
  const ratio = getMediaRatio(block)
  return (
    <PreviewContainer fullSize={fullSize}>
      <div
        className={
          fullSize
            ? 'flex flex-col gap-6 md:flex-row md:items-stretch'
            : 'flex h-full gap-1'
        }
      >
        <div
          className={
            fullSize
              ? `flex w-full flex-col justify-center ${PREVIEW_TEXT_W_FULL[ratio]}`
              : `flex ${PREVIEW_TEXT_W_THUMB[ratio]} flex-col justify-center overflow-hidden px-1`
          }
        >
          <MarkdownPreviewText
            description={getBlockDescription(block)}
            fullSize={fullSize}
          />
        </div>
        <div
          className={
            fullSize
              ? `aspect-[4/3] w-full overflow-hidden rounded ${PREVIEW_IMG_W_FULL[ratio]}`
              : `${PREVIEW_IMG_W_THUMB[ratio]} overflow-hidden rounded-sm`
          }
        >
          <ImageContent src={block.main_image?.src} fullSize={fullSize} />
        </div>
      </div>
    </PreviewContainer>
  )
}

function ImageLeftPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  // Text & Image, image-left: image left, text right.
  const ratio = getMediaRatio(block)
  return (
    <PreviewContainer fullSize={fullSize}>
      <div
        className={
          fullSize
            ? 'flex flex-col gap-6 md:flex-row md:items-stretch'
            : 'flex h-full gap-1'
        }
      >
        <div
          className={
            fullSize
              ? `aspect-[4/3] w-full overflow-hidden rounded ${PREVIEW_IMG_W_FULL[ratio]}`
              : `${PREVIEW_IMG_W_THUMB[ratio]} overflow-hidden rounded-sm`
          }
        >
          <ImageContent src={block.main_image?.src} fullSize={fullSize} />
        </div>
        <div
          className={
            fullSize
              ? `flex w-full flex-col justify-center ${PREVIEW_TEXT_W_FULL[ratio]}`
              : `flex ${PREVIEW_TEXT_W_THUMB[ratio]} flex-col justify-center overflow-hidden px-1`
          }
        >
          <MarkdownPreviewText
            description={getBlockDescription(block)}
            fullSize={fullSize}
          />
        </div>
      </div>
    </PreviewContainer>
  )
}

function TextOnImagePreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  // Text & Image, text-on-image: image fills the area, text overlay centered.
  const description = getBlockDescription(block)
  return (
    <PreviewContainer fullSize={fullSize}>
      {/* Full-size (page editor): the image is positioned absolutely to FILL
          the area, while the overlay is the in-flow, height-driving sibling.
          This lets the text region grow the block when the inline editor opens
          (no fixed height / overflow clip cutting off the TipTap editor), while
          keeping the same look at rest via `min-h-[450px]`. The thumbnail path
          keeps its original fixed-height, image-driven layout untouched. */}
      <div
        className={
          fullSize
            ? 'relative min-h-[450px] w-full rounded'
            : 'relative h-full w-full overflow-hidden rounded'
        }
      >
        {block.main_image?.src ? (
          <img
            src={block.main_image.src}
            alt=""
            className={
              fullSize
                ? 'absolute inset-0 h-full w-full rounded object-cover'
                : 'h-full w-full object-cover'
            }
          />
        ) : (
          <div
            className={
              fullSize
                ? 'absolute inset-0 h-full w-full rounded bg-gray-300'
                : 'h-full w-full bg-gray-300'
            }
          />
        )}
        {/* Overlay must paint ABOVE the image and stay clickable so inline edit
            opens. z-10 + pointer-events-auto guard against any sibling/overlay
            swallowing the click on the editable text. */}
        <div
          className={
            fullSize
              ? 'relative z-10 flex min-h-[450px] items-center justify-center rounded bg-black/40 px-6 py-10 pointer-events-auto'
              : 'absolute inset-0 flex items-center justify-center bg-black/40 px-2 py-1'
          }
        >
          <div className={fullSize ? 'w-full max-w-2xl text-center' : 'max-w-full text-center'}>
            {/* Full-size (page editor): always route through MarkdownPreviewText
                so it shows the CURRENT language, the editable placeholder, and
                inline edit via context. The thumbnail path keeps the original
                name-fallback gate (byte-for-byte unchanged for other consumers). */}
            {fullSize || description ? (
              <MarkdownPreviewText
                description={description}
                inverse
                fullSize={fullSize}
                className="text-center"
                textColor={block.text_color || undefined}
              />
            ) : (
              <p
                className={
                  fullSize
                    ? 'text-2xl font-medium text-white'
                    : 'text-[9px] leading-tight text-white line-clamp-2'
                }
              >
                {getBlockName(block)}
              </p>
            )}
          </div>
        </div>
      </div>
    </PreviewContainer>
  )
}

function MasonryPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  return (
    <PreviewContainer fullSize={fullSize}>
      <div
        className={
          fullSize ? 'flex h-[450px] gap-2' : 'flex h-full gap-0.5'
        }
      >
        <div
          className={
            fullSize
              ? 'w-1/2 overflow-hidden rounded'
              : 'w-1/2 overflow-hidden rounded-sm'
          }
        >
          <ImageContent src={block.gallery_images?.[0]?.src} fullSize={fullSize} />
        </div>
        <div className={fullSize ? 'flex w-1/2 flex-col gap-2' : 'flex w-1/2 flex-col gap-0.5'}>
          <div className={fullSize ? 'h-1/2 overflow-hidden rounded' : 'h-1/2 overflow-hidden rounded-sm'}>
            <ImageContent src={block.gallery_images?.[1]?.src} fullSize={fullSize} />
          </div>
          <div className={fullSize ? 'h-1/2 overflow-hidden rounded' : 'h-1/2 overflow-hidden rounded-sm'}>
            <ImageContent src={block.gallery_images?.[2]?.src} fullSize={fullSize} />
          </div>
        </div>
      </div>
    </PreviewContainer>
  )
}

function ScrollRhythmPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  return (
    <PreviewContainer fullSize={fullSize}>
      <div
        className={
          fullSize ? 'flex h-[450px] items-start gap-2' : 'flex h-full items-start gap-0.5'
        }
      >
        <div
          className={
            fullSize
              ? 'h-full w-1/2 overflow-hidden rounded'
              : 'h-full w-1/2 overflow-hidden rounded-sm'
          }
        >
          <ImageContent src={block.gallery_images?.[0]?.src} fullSize={fullSize} />
        </div>
        <div
          className={
            fullSize
              ? 'h-2/3 w-1/4 overflow-hidden rounded'
              : 'h-2/3 w-1/4 overflow-hidden rounded-sm'
          }
        >
          <ImageContent src={block.gallery_images?.[1]?.src} fullSize={fullSize} />
        </div>
        <div
          className={
            fullSize
              ? 'h-2/3 w-1/4 overflow-hidden rounded'
              : 'h-2/3 w-1/4 overflow-hidden rounded-sm'
          }
        >
          <ImageContent src={block.gallery_images?.[2]?.src} fullSize={fullSize} />
        </div>
      </div>
    </PreviewContainer>
  )
}

// Static row of 2-4 equal-width images, no carousel/pagination. Distinct from
// `SideBySidePreview` (which renders the `text_and_image` type's text+image
// layout for the unrelated `side_by_side` style value) — this one is keyed off
// `gallery_side_by_side` and only ever shows photos from `gallery_images`.
function GallerySideBySidePreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  const images = (block.gallery_images ?? []).slice(0, 4)
  const slots = images.length > 0 ? images : [undefined, undefined]
  return (
    <PreviewContainer fullSize={fullSize}>
      <div className={fullSize ? 'flex h-[450px] gap-2' : 'flex h-full gap-0.5'}>
        {slots.map((image, i) => (
          <div
            key={image?.id ?? i}
            className={
              fullSize
                ? 'flex-1 overflow-hidden rounded'
                : 'flex-1 overflow-hidden rounded-sm'
            }
          >
            <ImageContent src={image?.src} fullSize={fullSize} />
          </div>
        ))}
      </div>
    </PreviewContainer>
  )
}

// Literal map (not template-string interpolation) so Tailwind's JIT source
// scan keeps these classes. Defaults to 4 columns for missing/invalid values.
const PAGE_GRID_COLS_CLASS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
}

function PageGridPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  const { data } = useQuery(GET_PAGE_GRID_THUMBNAILS)

  const imageById = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of data?.findManyPage ?? []) {
      if (p?.hero_image?.src) map.set(p.id, p.hero_image.src)
    }
    return map
  }, [data])

  // config may be a JS object or a JSON string at runtime — handle both.
  let config: {
    mode?: string
    page_ids?: string[]
    max_pages?: number
    items_per_row?: number
  } | null = null
  const rawConfig = (block as { config?: unknown }).config
  if (rawConfig && typeof rawConfig === 'object') {
    config = rawConfig as {
      mode?: string
      page_ids?: string[]
      max_pages?: number
      items_per_row?: number
    }
  } else if (typeof rawConfig === 'string') {
    try {
      config = JSON.parse(rawConfig)
    } catch {
      config = null
    }
  }

  const isChildren = config?.mode === 'children'
  const ids = Array.isArray(config?.page_ids) ? config!.page_ids! : []
  const colsClass =
    PAGE_GRID_COLS_CLASS[config?.items_per_row ?? 4] ?? PAGE_GRID_COLS_CLASS[4]

  // In manual mode show real selected-page photos; in children mode (or with
  // no selection) fall back to representative gray tiles.
  const srcs = isChildren ? [] : ids.map(id => imageById.get(id) ?? null)
  const tiles = (srcs.length > 0 ? srcs : new Array(4).fill(null)).slice(0, 8)

  return (
    <PreviewContainer fullSize={fullSize}>
      <div className={fullSize ? `grid ${colsClass} gap-4` : `grid h-full ${colsClass} gap-0.5`}>
        {tiles.map((src, i) => (
          <div
            key={i}
            className={
              fullSize
                ? 'aspect-[4/3] overflow-hidden rounded'
                : 'overflow-hidden rounded-sm'
            }
          >
            <ImageContent src={src} fullSize={fullSize} />
          </div>
        ))}
      </div>
    </PreviewContainer>
  )
}

function ProductGridPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  const { data } = useQuery(GET_PRODUCT_GRID_THUMBNAILS)

  const containers: any[] = data?.findManyProductContainer ?? []

  const thumbOf = (c: any): string | null =>
    c?.single_product?.images?.[0]?.src_thumbnail ??
    c?.advanced_product?.images?.[0]?.src_thumbnail ??
    null

  const imageById = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of data?.findManyProductContainer ?? []) {
      const t = thumbOf(c)
      if (t) map.set(c.id, t)
    }
    return map
  }, [data])

  // config may be an object or JSON string
  let config: { mode?: string; max_products?: number } | null = null
  const rawConfig = (block as { config?: unknown }).config
  if (rawConfig && typeof rawConfig === 'object') {
    config = rawConfig as { mode?: string; max_products?: number }
  } else if (typeof rawConfig === 'string') {
    try {
      config = JSON.parse(rawConfig)
    } catch {
      config = null
    }
  }

  const max = config?.max_products ?? 8
  const isManual = config?.mode === 'manual'

  let srcs: (string | null)[]
  if (isManual) {
    // exact: the block's manually-picked grid products, in order
    srcs = (block.grid_product_containers ?? []).map(
      pc => imageById.get(pc.id) ?? null
    )
  } else {
    // newest / dynamic: a sample of real product photos
    srcs = containers
      .map(thumbOf)
      .filter((s): s is string => !!s)
      .slice(0, max)
  }

  const tiles = (srcs.length > 0 ? srcs : new Array(4).fill(null)).slice(0, max)

  return (
    <PreviewContainer fullSize={fullSize}>
      <div className={fullSize ? 'grid grid-cols-4 gap-4' : 'grid h-full grid-cols-4 gap-0.5'}>
        {tiles.map((src, i) => (
          <div
            key={i}
            className={
              fullSize
                ? 'aspect-[3/4] overflow-hidden rounded'
                : 'overflow-hidden rounded-sm'
            }
          >
            <ImageContent src={src} fullSize={fullSize} />
          </div>
        ))}
      </div>
    </PreviewContainer>
  )
}

// Mirrors `SideBySidePreview`/`ImageLeftPreview`'s image+text split, fixed at
// an even 50/50 (this block has no `mediaRatio` config), with the block's
// real copy mocked up as: heading/body (description, via the SAME
// `MarkdownPreviewText` every other text_and_image style preview uses) → a
// static input-shaped bar → a static rounded button shape. Only the
// description is shown (no title/eyebrow) — the block renders on the portal
// from description alone. Routing the description through
// `MarkdownPreviewText` is what gives this preview the same inline
// click-to-edit behavior (via `EditableDescription`) that `text_and_image`
// previews already have in the full-size page editor.
function EmailSignupPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  return (
    <PreviewContainer fullSize={fullSize}>
      <div
        className={
          fullSize
            ? 'flex flex-col gap-6 md:flex-row md:items-stretch'
            : 'flex h-full gap-1'
        }
      >
        <div
          className={
            fullSize
              ? 'aspect-[4/3] w-full overflow-hidden rounded md:w-1/2'
              : 'w-1/2 overflow-hidden rounded-sm'
          }
        >
          <ImageContent src={block.main_image?.src} fullSize={fullSize} />
        </div>
        <div
          className={
            fullSize
              ? 'flex w-full flex-col justify-center gap-3 md:w-1/2'
              : 'flex w-1/2 flex-col justify-center gap-1 overflow-hidden px-1'
          }
        >
          <MarkdownPreviewText
            description={getBlockDescription(block)}
            fullSize={fullSize}
          />

          {/* Mock email input */}
          <div
            className={
              fullSize
                ? 'mt-2 h-10 w-full rounded border border-gray-300 bg-white'
                : 'mt-1 h-2 w-full rounded-sm border border-gray-300 bg-white'
            }
          />

          {/* Mock submit button */}
          <div
            className={
              fullSize
                ? 'h-9 w-28 rounded-full bg-gray-800'
                : 'h-2 w-8 rounded-full bg-gray-700'
            }
          />
        </div>
      </div>
    </PreviewContainer>
  )
}

function PdfFlipbookPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  const config = getBlockConfig(block)
  const pdfUrl = config.pdf_url
  // Absent/unknown ⇒ 'flipbook' (backward compat — see `pdf_flipbook_viewer_mode`
  // in contentBlockPayload.ts). Only the literal 'plain' switches the mock.
  const isPlainPdf = config.viewer_mode === 'plain'
  const fileName =
    (typeof pdfUrl === 'string' ? pdfUrl : undefined)?.split('/').pop() ??
    'No PDF uploaded'
  return (
    <PreviewContainer fullSize={fullSize}>
      <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
        {isPlainPdf ? (
          /* Mock plain document: a single flat page with a folded corner */
          <div
            className={
              fullSize
                ? 'relative h-24 w-16 rounded border bg-white shadow-sm'
                : 'relative h-6 w-4 rounded-sm border bg-white'
            }
          >
            <div
              className={
                fullSize
                  ? 'absolute right-0 top-0 h-3 w-3 border-b border-l bg-gray-100'
                  : 'absolute right-0 top-0 h-1.5 w-1.5 border-b border-l bg-gray-100'
              }
            />
          </div>
        ) : (
          /* Mock open book: two facing pages */
          <div className={fullSize ? 'flex gap-1' : 'flex gap-0.5'}>
            <div className={fullSize ? 'h-24 w-16 rounded-l border bg-white shadow-sm' : 'h-6 w-4 rounded-l-sm border bg-white'} />
            <div className={fullSize ? 'h-24 w-16 rounded-r border bg-gray-50 shadow-sm' : 'h-6 w-4 rounded-r-sm border bg-gray-50'} />
          </div>
        )}
        {fullSize && <p className="mt-2 max-w-full truncate text-xs text-muted-foreground">{fileName}</p>}
      </div>
    </PreviewContainer>
  )
}

function ButtonPreview({
  block,
  variant,
  fullSize,
}: {
  block: ContentBlockItem
  variant: 'outline' | 'filled' | 'default'
  fullSize?: boolean
}) {
  const sizeClass = fullSize
    ? 'gap-3 px-8 py-4 text-sm'
    : 'gap-2 px-6 py-2 text-xs'
  const pillClassName =
    variant === 'filled'
      ? `inline-flex items-center rounded-full tracking-wide ${sizeClass}`
      : variant === 'outline'
        ? `inline-flex items-center border border-current rounded-full tracking-wide ${sizeClass}`
        : `inline-flex items-center border border-gray-400 rounded-full tracking-wide text-gray-600 ${sizeClass}`

  const pillStyle: React.CSSProperties =
    variant === 'filled'
      ? {
          backgroundColor: block.background_color || '#1a1a1a',
          color: block.text_color || '#ffffff',
        }
      : variant === 'outline'
        ? { color: block.text_color || '#1a1a1a' }
        : {}

  return (
    <PreviewContainer fullSize={fullSize}>
      <div className={fullSize ? 'flex items-center justify-center py-8' : 'flex items-center justify-center h-full'}>
        <div className={pillClassName} style={pillStyle}>
          {getBlockName(block)}
          <span>&rarr;</span>
        </div>
      </div>
    </PreviewContainer>
  )
}

function TextOnlyPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  const config = getBlockConfig(block)
  const titleAlignment = config.titleAlignment as TitleAlign | undefined
  // The frontend no longer renders a title/name for only_text (any of its
  // styles) — the whole text block is aligned via `titleAlignment`, falling
  // back to centered to match the frontend's default/1_column style fallback.
  const textAlignClass =
    titleAlignment === 'left'
      ? 'text-left'
      : titleAlignment === 'right'
        ? 'text-right'
        : 'text-center'
  return (
    <PreviewContainer fullSize={fullSize}>
      <div
        className={
          fullSize
            ? 'flex flex-col justify-center px-6 py-6'
            : 'flex h-full flex-col justify-center px-1'
        }
      >
        <div
          className={`w-full ${fullSize ? 'mx-auto max-w-2xl' : ''} ${textAlignClass}`}
        >
          <MarkdownPreviewText
            description={getBlockDescription(block)}
            fullSize={fullSize}
            textColor={block.text_color || undefined}
          />
        </div>
      </div>
    </PreviewContainer>
  )
}

/* ------------------------------------------------------------------ */
/*  Preview key helper                                                 */
/* ------------------------------------------------------------------ */

// Only these types offer a style dropdown in the editor (see
// `showStyleDropdown` in ContentBlockFormBody.tsx, gated on
// only_text/gallery/text_and_image/button) — for them `style` legitimately
// picks the preview variant. Every other type renders a single, fixed
// preview keyed on `type`, so a stale/leftover `style` (e.g. left over from
// a type change) must never shadow it.
const STYLE_DRIVEN_TYPES = new Set([
  'only_text',
  'gallery',
  'text_and_image',
  'button',
])

function getPreviewKey(block: ContentBlockItem): string {
  if (STYLE_DRIVEN_TYPES.has(block.type) && block.style) return block.style
  return block.type
}

/* ------------------------------------------------------------------ */
/*  ContentBlockPreview                                                */
/* ------------------------------------------------------------------ */

export function ContentBlockPreview({
  block,
  fullSize,
}: {
  block: ContentBlockItem
  fullSize?: boolean
}) {
  const key = getPreviewKey(block)

  switch (key) {
    // --- shared layouts ------------------------------------------------
    case 'side_by_side':
    case 'text_and_image':
      return <SideBySidePreview block={block} fullSize={fullSize} />

    case 'image_left':
      return <ImageLeftPreview block={block} fullSize={fullSize} />

    case 'masonry':
    case 'gallery':
      return <MasonryPreview block={block} fullSize={fullSize} />

    case 'scroll_rhythm':
      return <ScrollRhythmPreview block={block} fullSize={fullSize} />

    case 'gallery_side_by_side':
      return <GallerySideBySidePreview block={block} fullSize={fullSize} />

    case 'outline':
      return <ButtonPreview block={block} variant="outline" fullSize={fullSize} />

    case 'filled':
      return <ButtonPreview block={block} variant="filled" fullSize={fullSize} />

    case 'button':
      return <ButtonPreview block={block} variant="outline" fullSize={fullSize} />

    case '1_column_title_top':
      return <TextOnlyPreview block={block} fullSize={fullSize} />

    case 'only_text':
      return <TextOnlyPreview block={block} fullSize={fullSize} />

    // --- unique layouts ------------------------------------------------
    case 'text_on_image':
      return <TextOnImagePreview block={block} fullSize={fullSize} />

    // All multi-column text styles render N text columns and no title (the
    // block `name` is not rendered by the portal for these styles, so the
    // editor must not offer a title column either). See MultiColumnTextPreview.
    case '2_columns_title_top_center':
      return (
        <MultiColumnTextPreview block={block} fullSize={fullSize} columns={2} />
      )

    // Narrow left / wide right (1/3 + 2/3) split — the unequal widths only
    // matter in the portal; the admin thumbnail hints at them via
    // `thumbWidths`, and the full-size editor stays an equal 2-column grid
    // like any other 2-section text style.
    case '2_columns_1_3':
      return (
        <MultiColumnTextPreview
          block={block}
          fullSize={fullSize}
          columns={2}
          thumbWidths={['w-1/3', 'w-2/3']}
        />
      )

    case '3_columns_title_left':
      return (
        <MultiColumnTextPreview block={block} fullSize={fullSize} columns={3} />
      )

    case 'spread_harmony':
      return (
        <PreviewContainer fullSize={fullSize}>
          <div className={fullSize ? 'flex h-[450px] gap-2' : 'flex h-full gap-0.5'}>
            <div className={fullSize ? 'w-1/2 overflow-hidden rounded' : 'w-1/2 overflow-hidden rounded-sm'}>
              <ImageContent src={block.gallery_images?.[0]?.src} fullSize={fullSize} />
            </div>
            <div className={fullSize ? 'grid w-1/2 grid-cols-2 grid-rows-2 gap-2' : 'grid w-1/2 grid-cols-2 grid-rows-2 gap-0.5'}>
              <div className={fullSize ? 'overflow-hidden rounded' : 'overflow-hidden rounded-sm'}>
                <ImageContent src={block.gallery_images?.[1]?.src} fullSize={fullSize} />
              </div>
              <div className="rounded-sm" />
              <div className="rounded-sm" />
              <div className={fullSize ? 'overflow-hidden rounded' : 'overflow-hidden rounded-sm'}>
                <ImageContent src={block.gallery_images?.[2]?.src} fullSize={fullSize} />
              </div>
            </div>
          </div>
        </PreviewContainer>
      )

    case 'only_image':
      return (
        <PreviewContainer fullSize={fullSize}>
          <div className={fullSize ? 'flex items-center justify-center' : 'flex h-full items-center justify-center'}>
            <ImageContent
              src={block.main_image?.src}
              fullSize={fullSize}
              className={
                fullSize
                  ? 'max-h-[600px] w-auto max-w-full rounded object-contain'
                  : 'h-full rounded-sm object-contain'
              }
            />
          </div>
        </PreviewContainer>
      )

    case 'only_video':
      return (
        <PreviewContainer fullSize={fullSize}>
          <div
            className={
              fullSize
                ? 'relative aspect-video w-full rounded bg-gray-300'
                : 'relative h-full w-full rounded-sm bg-gray-300'
            }
          >
            <PlayButton fullSize={fullSize} />
          </div>
        </PreviewContainer>
      )

    case 'text_and_video':
      return (
        <PreviewContainer fullSize={fullSize}>
          <div className={fullSize ? 'flex flex-col gap-6 md:flex-row md:items-stretch' : 'flex h-full gap-1'}>
            <div className={fullSize ? 'flex w-full flex-col justify-center md:w-1/2' : 'flex w-1/2 flex-col justify-center px-1'}>
              <TextContent block={block} fullSize={fullSize} />
            </div>
            <div
              className={
                fullSize
                  ? 'relative aspect-video w-full rounded bg-gray-300 md:w-1/2'
                  : 'relative w-1/2 rounded-sm bg-gray-300'
              }
            >
              <PlayButton fullSize={fullSize} />
            </div>
          </div>
        </PreviewContainer>
      )

    case 'category_tiles': {
      const config = getBlockConfig(block)
      const hideTitle = Boolean(config.hideTitle)
      const titleAlignment = config.titleAlignment as TitleAlign | undefined
      // Natural look is left; the title `p` is full-width, so text-align suffices.
      const textAlign =
        titleAlignment === 'center'
          ? 'text-center'
          : titleAlignment === 'right'
            ? 'text-right'
            : 'text-left'
      return (
        <PreviewContainer fullSize={fullSize}>
          <div className={fullSize ? 'flex flex-col gap-4' : 'flex h-full flex-col gap-1 p-1'}>
            {!hideTitle && (
              <EditableTitle
                as="p"
                name={getBlockName(block)}
                className={
                  fullSize
                    ? `${textAlign} text-sm font-medium uppercase tracking-[0.2em] text-gray-700`
                    : `${textAlign} text-[8px] font-medium text-gray-600 truncate`
                }
              />
            )}
            <div className={fullSize ? 'grid grid-cols-2 gap-4 md:grid-cols-4' : 'grid flex-1 grid-cols-2 gap-0.5'}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className={fullSize ? 'h-[450px] rounded bg-gray-300' : 'rounded-sm bg-gray-300'}
                />
              ))}
            </div>
          </div>
        </PreviewContainer>
      )
    }

    case 'page_grid':
      return <PageGridPreview block={block} fullSize={fullSize} />

    case 'product_grid':
      return <ProductGridPreview block={block} fullSize={fullSize} />

    case 'email_signup':
      return <EmailSignupPreview block={block} fullSize={fullSize} />

    case 'pdf_flipbook':
      return <PdfFlipbookPreview block={block} fullSize={fullSize} />

    default:
      return <TextOnlyPreview block={block} fullSize={fullSize} />
  }
}

export function DraggableContentBlockItem({
  block,
  index,
  onRemove,
  onEdit,
  onClose,
  fullSize,
  selected,
  onDescriptionSaved,
  onStageDescription,
  onStageName,
}: Props) {
  const { language } = useLanguage()

  // The current language's profile (if any) and its raw description / name.
  const currentProfile = block.content_block_profiles.find(
    p => p.language === language
  )
  const rawValue = currentProfile?.description ?? ''
  // Title editing edits the CURRENT language's name — '' when that language has
  // no profile yet (shows the "Click to add title" placeholder), independent of
  // getBlockName's English fallback used for static display.
  const rawTitleValue = currentProfile?.name ?? ''

  // Inline edit is allowed in the full-size page editor — including while the
  // block is selected/active. The right-panel settings form no longer edits the
  // description (it's preview-only here), so the two editors can't fight.
  const editable = Boolean(fullSize)

  // Multi-section styles (2-/3-column text) pack N independent columns into the
  // single `description` scalar via an in-string delimiter; every other style is
  // single-section. Drives per-section slicing/saving below.
  const sectionCount = sectionCountForStyle(getPreviewKey(block))

  // Block-level shared edit state for the multi-column text styles. ALL section
  // drafts live here (not inside each EditableDescription) so the editors
  // open, edit, save, and cancel as ONE unit under a single shared action row.
  const [multiEditing, setMultiEditing] = useState(false)
  const [multiDrafts, setMultiDrafts] = useState<string[]>([])
  const [multiSaving, setMultiSaving] = useState(false)

  // STAGE a FULL description for the current language — no server write here.
  // Optimistically merges the edit into the owner's block state (so the
  // preview shows it immediately) AND records it in the page's per-block
  // draft, which the page-level Save later flushes via
  // `UPDATE_CONTENT_BLOCK_PROFILE` (same mutation this used to fire directly —
  // see `PageDetails.onSave`'s flush for the update-vs-create branching this
  // used to do here). Shared by the single-section save (handleSaveSection)
  // and the multi-section shared save (saveMultiEdit).
  //
  // A synthetic `id: -1` marks a profile that doesn't exist on the server yet
  // (this language had none) — never a real id, since real ids are always
  // positive. The flush uses this same marker (via the block's CURRENT
  // profiles at save time) to decide update vs. create.
  const persistDescription = async (next: string) => {
    const updatedProfiles = currentProfile
      ? block.content_block_profiles.map(p =>
          p.language === language
            ? { ...p, description: next, description_format: 'markdown' }
            : p
        )
      : [
          ...block.content_block_profiles,
          { id: -1, name: '', description: next, description_format: 'markdown', language },
        ]

    onDescriptionSaved?.({ ...block, content_block_profiles: updatedProfiles })
    onStageDescription?.(block.id, language, next)
  }

  // Persist a new description SECTION for the current language (single-section
  // styles). For those `index` is always 0 and no delimiter is written.
  const handleSaveSection = async (index: number, text: string) => {
    const next = setSection(
      currentProfile?.description ?? '',
      index,
      text,
      sectionCount
    )
    await persistDescription(next)
  }

  // --- Multi-section (2-/3-column text) shared edit controller -------------
  // Open ALL section editors together, snapshotting the live drafts from the
  // stored value (delimiter split, padded to sectionCount).
  const startMultiEdit = () => {
    if (multiSaving) return
    const parts = splitSections(rawValue)
    while (parts.length < sectionCount) parts.push('')
    setMultiDrafts(parts.slice(0, sectionCount))
    setMultiEditing(true)
  }

  const setMultiDraft = (index: number, value: string) => {
    setMultiDrafts(prev => {
      const nextArr = [...prev]
      while (nextArr.length < sectionCount) nextArr.push('')
      nextArr[index] = value
      return nextArr
    })
  }

  const cancelMultiEdit = () => {
    setMultiEditing(false)
    setMultiDrafts([])
  }

  // Build the FULL description from the LIVE drafts (never the stale stored
  // value, so neither section clobbers the other) and persist in ONE mutation.
  const saveMultiEdit = async () => {
    if (multiSaving) return
    const next = joinSections(
      Array.from({ length: sectionCount }, (_, i) => multiDrafts[i] ?? '')
    )
    if (next === rawValue) {
      // Nothing changed — just close, no mutation.
      setMultiEditing(false)
      setMultiDrafts([])
      return
    }
    setMultiSaving(true)
    try {
      await persistDescription(next)
      setMultiEditing(false)
      setMultiDrafts([])
    } catch {
      // Keep drafts + stay in edit mode so the user can retry.
    } finally {
      setMultiSaving(false)
    }
  }

  const editContextValue = {
    rawValue,
    editable,
    sectionCount,
    onSaveSection: handleSaveSection,
    multi: {
      editing: multiEditing,
      saving: multiSaving,
      drafts: multiDrafts,
      start: startMultiEdit,
      cancel: cancelMultiEdit,
      save: saveMultiEdit,
      setDraft: setMultiDraft,
    },
  }

  // STAGE a new NAME (title) for the current language — no server write here.
  // Title is plain single-line text, so description / description_format are
  // left untouched (mirrors the old immediate-save behavior; no clobbering the
  // inline description editor's staged content). See `persistDescription`
  // above for the shared synthetic-id / flush explanation.
  const handleSaveTitle = async (next: string) => {
    const updatedProfiles = currentProfile
      ? block.content_block_profiles.map(p =>
          p.language === language ? { ...p, name: next } : p
        )
      : [...block.content_block_profiles, { id: -1, name: next, language }]

    onDescriptionSaved?.({ ...block, content_block_profiles: updatedProfiles })
    onStageName?.(block.id, language, next)
  }

  const titleContextValue = {
    rawValue: rawTitleValue,
    editable,
    onSave: handleSaveTitle,
  }

  return (
    <Draggable draggableId={String(block.id)} index={index}>
      {provided => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`group relative rounded-lg bg-white overflow-hidden ${
            selected ? 'ring-2 ring-primary' : ''
          }`}
        >
          {/* Drag affordance.
              - fullSize (page editor): a small grip (top-left) is the only drag
                handle, so the realistic full-size preview underneath stays clean.
              - thumbnail (other consumers): the whole preview is the handle, as
                before — keeps ContentBlocksManager / CategoryForm unchanged. */}
          {fullSize ? (
            <>
              <div
                {...provided.dragHandleProps}
                className="absolute top-1.5 left-1.5 z-10 flex h-7 w-7 cursor-grab items-center justify-center rounded bg-white/90 text-gray-500 shadow-sm opacity-0 transition-opacity group-hover:opacity-100"
              >
                <GripVertical className="h-3.5 w-3.5" />
              </div>
              <EditableTitleProvider value={titleContextValue}>
                <EditableDescriptionProvider value={editContextValue}>
                  <ContentBlockPreview block={block} fullSize />
                </EditableDescriptionProvider>
              </EditableTitleProvider>
            </>
          ) : (
            <div {...provided.dragHandleProps} className="relative cursor-grab">
              <ContentBlockPreview block={block} />
              <div className="pointer-events-none absolute bottom-1.5 left-1.5">
                <span className="text-[10px] font-medium text-gray-600 bg-white/80 rounded px-1.5 py-0.5">
                  {(block.content_block_profiles.find(p => p.language === 'en')?.name ||
                    block.content_block_profiles[0]?.name)
                    ? `${getBlockName(block)} · #${block.id}`
                    : getBlockName(block)}
                </span>
              </div>
            </div>
          )}

          {/* Hover controls (top-right).
              - fullSize (page editor): a single gear (Settings) button that
                opens the block in the right panel; when the block is selected it
                becomes a Close (✕) that deselects → returns to page settings.
                Delete now lives in the right sidebar, so there's no trash here.
              - thumbnail (other consumers): the original Edit (pencil) + Remove
                (trash) controls, byte-for-byte unchanged. */}
          {fullSize ? (
            <div className="absolute top-1.5 right-1.5 z-10 flex opacity-0 group-hover:opacity-100 transition-opacity">
              {selected ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={() => onClose?.()}
                  title="Close"
                  aria-label="Close"
                  className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              ) : (
                onEdit && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => onEdit(block.id)}
                    title="Block settings"
                    aria-label="Block settings"
                    className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm"
                  >
                    <Settings className="h-3.5 w-3.5" />
                  </Button>
                )
              )}
            </div>
          ) : (
            <div className="absolute top-1.5 right-1.5 z-10 flex opacity-0 group-hover:opacity-100 transition-opacity">
              {onEdit && (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={() => onEdit(block.id)}
                  className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={() => onRemove(block.id)}
                className="h-7 w-7 bg-white/90 hover:bg-red-50 shadow-sm text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </Draggable>
  )
}
